/**
 * Grade colour palette state — CR-7079.
 *
 * The palette is the single place a customer sets what colour each grade is
 * drawn in. Saving one entry recolours that grade everywhere it appears:
 * the Supplier Performance rating column, the dashboard OTIF and OTD gauges
 * and the PPM bands, because each of those resolves its displayed colour from
 * this table server-side.
 *
 * @param onSaved - called after a successful save or reset so the parent can
 *   reload settings and pick up the new `effective_color` on every band.
 * @param labelFor - resolves a grade id to the customer's own label, so the
 *   confirmation names the grade the way they do ('Gold', not 'B').
 * @returns palette rows, per-row draft state, and the save/reset actions.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { performanceSettingsAPI, GradeColour } from '../../../lib/api';

export interface UseGradeColourPalette {
  palette: GradeColour[];
  drafts: Record<string, string>;
  loading: boolean;
  saving: boolean;
  resetting: boolean;
  setDraft: (grade: string, color: string) => void;
  save: (entry: GradeColour) => Promise<void>;
  resetDefaults: () => Promise<void>;
  isDirty: (entry: GradeColour) => boolean;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export function useGradeColourPalette(
  onSaved: () => void,
  labelFor: (grade: string) => string = (g) => g,
): UseGradeColourPalette {
  const [palette, setPalette] = useState<GradeColour[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await performanceSettingsAPI.getGradeColours();
      setPalette(response.data.results);
      setDrafts({});
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Could not load grade colours: ${message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setDraft = useCallback((grade: string, color: string) => {
    setDrafts((prev) => ({ ...prev, [grade]: color }));
  }, []);

  const isDirty = useCallback(
    (entry: GradeColour) => {
      const draft = drafts[entry.grade];
      return draft !== undefined && draft.toUpperCase() !== entry.color.toUpperCase();
    },
    [drafts],
  );

  const save = useCallback(
    async (entry: GradeColour) => {
      const color = drafts[entry.grade] ?? entry.color;
      if (!HEX.test(color)) {
        toast.error('Enter a 6-digit hex colour, for example #D4AF37.');
        return;
      }
      setSaving(true);
      try {
        await performanceSettingsAPI.updateGradeColour(entry.id, { color });
        toast.success(`${labelFor(entry.grade)} recoloured across all scorecards`);
        await load();
        onSaved();
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        toast.error(`Could not save colour: ${message}`);
      } finally {
        setSaving(false);
      }
    },
    [drafts, load, onSaved, labelFor],
  );

  const resetDefaults = useCallback(async () => {
    setResetting(true);
    try {
      await performanceSettingsAPI.resetGradeColours();
      toast.success('Grade colours reset to the baseline palette');
      await load();
      onSaved();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Could not reset colours: ${message}`);
    } finally {
      setResetting(false);
    }
  }, [load, onSaved]);

  return { palette, drafts, loading, saving, resetting, setDraft, save, resetDefaults, isDirty };
}
